export default function POIPreview({ poi, metadata }) {
  const { name, category, subcategory, description, lat, lng, address } = poi;
  
  // Parse metadata if it's a string
  let parsedMetadata = {};
  if (typeof metadata === 'string') {
    try {
      parsedMetadata = JSON.parse(metadata);
    } catch (e) {
      parsedMetadata = {};
    }
  } else {
    parsedMetadata = metadata || {};
  }

  const categoryInfo = {
    metro: { icon: 'Ⓜ️', label: 'Μετρό', color: '#00a651' },
    rail: { icon: '🚂', label: 'Προαστιακός', color: '#ff9800' },
    tram: { icon: '🚊', label: 'Τραμ', color: '#f44336' },
    ev_charger: { icon: '⚡', label: 'Φορτιστής EV', color: '#10b981' },
    parking: { icon: '🅿️', label: 'Πάρκινγκ', color: '#3b82f6' },
    bike_station: { icon: '🚲', label: 'Σταθμός Ποδηλάτων', color: '#8b5cf6' }
  };

  const info = categoryInfo[category] || { icon: '📍', label: category, color: '#6b7280' };

  const renderMetadataField = (key, value) => {
    if (value === null || value === undefined || value === '') return null;
    
    let displayValue = value;
    let displayKey = key;

    // Translate common keys
    const keyTranslations = {
      line: 'Γραμμή',
      line_name: 'Όνομα Γραμμής',
      operator: 'Χειριστής',
      accessibility: 'Προσβασιμότητα',
      socket_types: 'Τύποι Πρίζας',
      power_kw: 'Ισχύς (kW)',
      pricing: 'Τιμολόγηση',
      capacity: 'Χωρητικότητα',
      opening_hours: 'Ώρες',
      phone: 'Τηλέφωνο',
      website: 'Ιστοσελίδα',
      email: 'Email',
      service_hours: 'Ωράριο'
    };

    displayKey = keyTranslations[key] || key;

    // Format values
    if (typeof value === 'boolean') {
      displayValue = value ? '✓' : '✗';
    } else if (Array.isArray(value)) {
      displayValue = value.join(', ');
    } else if (typeof value === 'object') {
      // Format nested objects like service_hours
      if (key === 'service_hours' && value.weekday && value.weekend) {
        return (
          <div key={key} style={{ borderBottom: '1px solid #e5e7eb', paddingTop: '4px', paddingBottom: '4px' }}>
            <div style={{ fontWeight: '500', fontSize: '12px', color: '#374151' }}>{displayKey}:</div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
              <div>Καθημερινές: {value.weekday}</div>
              <div>Σαββατοκύριακα: {value.weekend}</div>
            </div>
          </div>
        );
      }
      displayValue = JSON.stringify(value);
    }

    return (
      <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', borderBottom: '1px solid #f3f4f6', paddingTop: '4px', paddingBottom: '4px' }}>
        <span style={{ fontWeight: '500', fontSize: '12px', color: '#374151' }}>{displayKey}:</span>
        <span style={{ fontSize: '12px', color: '#111827', textAlign: 'right' }}>{displayValue}</span>
      </div>
    );
  };

  return (
    <div style={{ border: '1px solid #d1d5db', borderRadius: '8px', padding: '16px', background: 'linear-gradient(to bottom right, #ffffff, #f9fafb)', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', width: '260px', maxWidth: '260px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', paddingBottom: '8px', borderBottom: '2px solid #e5e7eb' }}>
        <div style={{ fontSize: '24px' }}>{info.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#111827', lineHeight: '1.2' }}>{name || 'Νέο POI'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
            <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: '4px', backgroundColor: info.color, color: 'white', fontSize: '10px', fontWeight: '600' }}>
              {info.label}
            </span>
            {subcategory && (
              <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: '600' }}>{subcategory}</span>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {description && (
        <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
          <p style={{ fontSize: '12px', color: '#374151', lineHeight: '1.4', margin: 0 }}>{description}</p>
        </div>
      )}

      {/* Location */}
      <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: '6px' }}>
          <span style={{ fontSize: '14px' }}>📍</span>
          <div style={{ flex: 1, fontSize: '11px' }}>
            <div style={{ fontWeight: '500', color: '#374151', marginBottom: '2px' }}>Τοποθεσία</div>
            {address && (
              <div style={{ color: '#6b7280', marginBottom: '2px' }}>{address}</div>
            )}
            <div style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '10px' }}>
              {lat && lng ? `${parseFloat(lat).toFixed(5)}, ${parseFloat(lng).toFixed(5)}` : 'Δεν έχει οριστεί'}
            </div>
          </div>
        </div>
      </div>

      {/* Metadata */}
      {Object.keys(parsedMetadata).length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px' }}>ℹ️</span>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Πληροφορίες</span>
          </div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '4px', padding: '6px' }}>
            {Object.entries(parsedMetadata).map(([key, value]) => 
              renderMetadataField(key, value)
            )}
          </div>
        </div>
      )}

      {Object.keys(parsedMetadata).length === 0 && (
        <div style={{ textAlign: 'center', paddingTop: '12px', paddingBottom: '12px', color: '#9ca3af', fontSize: '12px' }}>
          Δεν υπάρχουν πρόσθετες πληροφορίες
        </div>
      )}
    </div>
  );
}
